import {
  type SuggestedReviewersResponse as SuggestedReviewerData,
  SuggestedReviewersResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/suggested_reviewers'
import {useMemo} from 'react'
import {keepPreviousData, useQuery, type QueryKey} from '@github-ui/react-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {createProtoPersister} from '@github-ui/react-query-persister'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useAnalytics} from '@github-ui/use-analytics'

function suggestedReviewersQueryKey(apiUrl: string): QueryKey {
  return [PageData.suggestedReviewers, apiUrl]
}

// Suggested reviewers is an expensive query that is only updated when the PR changes, so we can cache it for a long time.
// This avoids hitting git blame and other expensive queries on every render of the reviewer picker.
// Future enhancement would be to invalidate this cache when `git_updated` alive event is triggered.
const SUGGESTED_REVIEWERS_STALE_TIME = 15 * 60 * 1000 // 15 minutes

/**
 * Reads the suggested reviewers from the dedicated `suggested_reviewers` page-data
 * endpoint. The backend serves proto3-JSON (flagged by the `X-Proto-JSON` header),
 * which `queryFnFetch` decodes through `SuggestedReviewersResponseSchema` into the
 * canonical proto `SuggestedReviewer` shape (int64 `reviewer.id` as a `bigint`,
 * default-valued fields normalized) rather than the raw JSON wire form.
 */
export function useSuggestedReviewers({enabled = true}: {enabled?: boolean} = {}) {
  const apiUrl = usePageDataUrl(PageData.suggestedReviewers)
  const {sendAnalyticsEvent} = useAnalytics()

  // Schema-bound proto response persists to IndexedDB (validated against the
  // schema, keyed by its hash) so revisits paint the last-known suggestions
  // immediately while the long stale time revalidates in the background.
  const persister = useMemo(
    () => createProtoPersister({schema: SuggestedReviewersResponseSchema, sendAnalyticsEvent}),
    [sendAnalyticsEvent],
  )

  return useQuery<SuggestedReviewerData>({
    queryKey: suggestedReviewersQueryKey(apiUrl),
    queryFn: () =>
      queryFnFetch(
        {queryDeps: {pathname: apiUrl}, routeId: 'pull-requests', queryName: 'suggestedReviewers'},
        {schema: SuggestedReviewersResponseSchema},
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: SUGGESTED_REVIEWERS_STALE_TIME,
    persister,
  })
}
